import { useState } from 'react';
import api from '../../../../../services/api';
import { updateRFPStatus, updateProposalStage, analyzeRFP, updateRFPAnalysis, updateRFPMetadata } from '../../../services/rfpCoreService';
import {
    updateSectionContent,
    updateSectionStatus,
    submitSectionToLead,
    requestClarification,
    requestExtension,
    handleSectionExtensionDecision
} from '../../../services/sectionService';
import { formatErrorDetail, formatForDateInput } from '../utils/helperUtils';
import { tabMapping, excludeKeys } from '../constants/rfpConstants';
import { smoothReload } from '../../../../../shared/utils/smoothReload';

/**
 * Hook to manage RFP update operations (status, stage, content)
 */
export const useRFPUpdates = (rfpId, editableData, setEditableData, setActiveSection, setAlertModal, isManager, activeSection, fetchRFPData, fetchOverview, setAssignments, fetchVersions, onSaveSuccess) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [isStageUpdating, setIsStageUpdating] = useState(false);
    const [isSubmittingToLead, setIsSubmittingToLead] = useState(false);
    const [isApprovingSection, setIsApprovingSection] = useState(false);
    const [updateStatus, setUpdateStatus] = useState(null);
    const [stageError, setStageError] = useState("");

    const handleStatusUpdate = async (newStatus) => {
        if (!rfpId) return;
        setIsUpdating(true);
        setUpdateStatus(null);

        try {
            const response = await updateRFPStatus(rfpId, newStatus);

            if (response.status === 200) {
                const data = response.data;
                setAlertModal({
                    show: true,
                    message: data.message || `The status has been successfully updated.`
                });
                setEditableData(prev => ({ ...prev, status: newStatus }));
            } else {
                setUpdateStatus({ type: 'error', message: 'Failed to update status.' });
            }
        } catch (err) {
            console.error('Status update error:', err);
            const errMsg = err.response?.data?.message || err.response?.data?.detail || 'Network error during status update.';
            setUpdateStatus({ type: 'error', message: formatErrorDetail(errMsg) });
        } finally {
            setIsUpdating(false);
            setTimeout(() => setUpdateStatus(null), 4000);
        }
    };

    const executeStageTransition = async (stage, notes, fromUpload = false, files = [], additionalBodyFields = {}) => {
        let stageValue = stage;
        if (['Client Accepted', 'Client Rejected', 'Proposal Accepted', 'Proposal Rejected', 'Lost'].includes(stage)) {
            if (!notes || notes.trim().length < 5) {
                setStageError("Please write something. (At least 5 letters required)");
                return false;
            }
            if (stage === 'Client Accepted' || stage === 'Proposal Accepted') {
                stageValue = 6;
            } else if (stage === 'Client Rejected' || stage === 'Proposal Rejected' || stage === 'Lost') {
                stageValue = 7;
            }
        }

        setIsStageUpdating(true);
        setUpdateStatus(null);

        try {
            const patchResponse = await updateProposalStage(rfpId, stageValue, stageValue, notes, additionalBodyFields);

            const patchData = patchResponse.data;
            const successMessage = patchData.message || (patchResponse.status === 200 ? `Stage successfully moved to "${stage}".` : "");

            if (patchResponse.status !== 200) {
                setUpdateStatus({ type: 'error', message: patchData.message || formatErrorDetail(patchData.detail) || 'Stage update failed.' });
                setIsStageUpdating(false);
                return false;
            }

            if (fromUpload) {
                const formData = new FormData();
                if (files && files.length > 0) {
                    files.forEach(f => formData.append('files', f));
                }
                const nextResponse = await analyzeRFP(formData);

                if (nextResponse && (nextResponse.status === 200 || nextResponse.status === 201)) {
                    const freshData = nextResponse.data;

                    // Normalization logic
                    if (freshData.VALUATION && !freshData.valuation) freshData.valuation = freshData.VALUATION;
                    if (freshData.PROPOSAL_DEADLINE && !freshData.proposal_deadline) freshData.proposal_deadline = freshData.PROPOSAL_DEADLINE;
                    if (freshData.QUERY_DEADLINE && !freshData.query_deadline) freshData.query_deadline = freshData.QUERY_DEADLINE;
                    if (freshData.CLIENT_NAME && !freshData.client_name) freshData.client_name = freshData.CLIENT_NAME;
                    if (freshData.CLIENT_LOCATION && !freshData.client_place) freshData.client_place = freshData.CLIENT_LOCATION;
                    if (freshData.PROPOSAL_TITLE && !freshData.proposal_title) freshData.proposal_title = freshData.PROPOSAL_TITLE;
                    if (freshData.PROPOSAL_STAGE && !freshData.proposal_stage) freshData.proposal_stage = freshData.PROPOSAL_STAGE;
                    if (freshData.CURRENCY && !freshData.currency) freshData.currency = freshData.CURRENCY;
                    if (freshData.INDUSTRY && !freshData.industry) freshData.industry = freshData.INDUSTRY;
                    if (freshData.CONFIDENCE && !freshData.confidence) freshData.confidence = freshData.CONFIDENCE;
                    if (freshData.REASONING && !freshData.reasoning) freshData.reasoning = freshData.REASONING;

                    setEditableData(prev => {
                        const updatedData = { ...freshData };
                        if (notes) updatedData.stage_notes = notes;
                        updatedData.proposal_deadline = updatedData.proposal_deadline && updatedData.proposal_deadline !== 'N/A'
                            ? updatedData.proposal_deadline
                            : (updatedData.extraction_data?.submission_deadline || updatedData.metadata?.proposal_deadline || prev.proposal_deadline || '');
                        updatedData.valuation = updatedData.valuation && updatedData.valuation !== 'N/A'
                            ? updatedData.valuation
                            : (updatedData.extraction_data?.valuation || updatedData.metadata?.valuation || prev.valuation || '');
                        updatedData.query_deadline = updatedData.query_deadline && updatedData.query_deadline !== 'N/A'
                            ? updatedData.query_deadline
                            : (updatedData.extraction_data?.query_deadline || updatedData.metadata?.query_deadline || prev.query_deadline || '');
                        return updatedData;
                    });

                    const newViewConfig = freshData.VIEW_CONFIG || freshData.view_config;
                    const newActiveTabs = newViewConfig?.active_tabs || [];
                    if (newActiveTabs.length > 0) {
                        const firstTabMappingValue = tabMapping[newActiveTabs[0]];
                        if (firstTabMappingValue) {
                            const actualKey = Object.keys(freshData).find(k => k.toUpperCase().trim() === firstTabMappingValue.toUpperCase().trim());
                            if (actualKey) setActiveSection(actualKey);
                        }
                    }

                    setAlertModal({ show: true, message: successMessage, notes: notes });
                    smoothReload(1200, 'Updating proposal stage…');
                    return true;
                } else {
                    setEditableData(prev => ({
                        ...prev,
                        proposal_stage: stage,
                        stage_notes: notes || prev.stage_notes
                    }));
                    setAlertModal({ show: true, message: `${successMessage} (Could not refresh data)`, notes: notes });
                    smoothReload(1200, 'Updating proposal stage…');
                    return true;
                }
            } else {
                // OPTIMISTIC LOCAL UPDATE
                setEditableData(prev => ({
                    ...prev,
                    proposal_stage: stage,
                    PROPOSAL_STAGE: stage,
                    stage_notes: notes || prev.stage_notes,
                    global_status: prev.global_status ? { ...prev.global_status, code: stageValue } : { code: stageValue }
                }));
                setAlertModal({ show: true, message: successMessage, notes: notes });
                setIsStageUpdating(false);
                return true;
            }
        } catch (err) {
                console.error('Stage transition error:', err);
                const status = err.response?.status;
                const detail = err.response?.data?.detail || err.response?.data?.message;
                let errorMsg = 'Network error during transition.';
                if (status === 409) {
                    if (Array.isArray(detail)) {
                        errorMsg = `Cannot finalise proposal. The following sections are still submitted: ${detail.join(', ')}`;
                    } else if (typeof detail === 'string') {
                        errorMsg = detail;
                    } else {
                        errorMsg = 'Cannot finalise proposal while sections are submitted.';
                    }
                } else if (detail) {
                    errorMsg = formatErrorDetail(detail);
                }
                setStageError(errorMsg);
                setUpdateStatus({ type: 'error', message: errorMsg });
                return false;
            } finally {
                setIsStageUpdating(false);
                setTimeout(() => setUpdateStatus(null), 4000);
            }
        };

        const handleUpdate = async (isEditingContent, setIsEditingContent, customContent = undefined) => {
            if (!rfpId) {
                setUpdateStatus({ type: 'error', message: 'RFP ID missing.' });
                return;
            }

            setIsUpdating(true);
            setUpdateStatus(null);

            try {
                // Check if we are updating a specific section content using the new API
                const isSectionUpdate = activeSection && activeSection !== 'Key Information';

                if (isSectionUpdate) {
                    const activeSectionObj = (editableData?.sections || []).find(s =>
                        s.label === activeSection ||
                        s.key === activeSection ||
                        s.section_title === activeSection ||
                        s.section_key === activeSection ||
                        String(s.id) === String(activeSection) ||
                        String(s.section_id) === String(activeSection)
                    );
                    const sectionId = activeSectionObj?.section_id || activeSectionObj?.id || activeSectionObj?._id;

                    if (sectionId) {
                        const sectionContent = customContent !== undefined ? customContent : editableData[activeSection];
                        let contentString = '';

                        if (typeof sectionContent === 'string') {
                            contentString = sectionContent;
                        } else if (Array.isArray(sectionContent)) {
                            // Table data stored as array of row objects — serialize to Markdown
                            if (sectionContent.length > 0) {
                                const headers = Object.keys(sectionContent[0]);
                                const headerRow = `| ${headers.join(' | ')} |`;
                                const separatorRow = `| ${headers.map(() => '---').join(' | ')} |`;
                                const dataRows = sectionContent.map(row =>
                                    `| ${headers.map(h => String(row[h] ?? '')).join(' | ')} |`
                                );
                                contentString = [headerRow, separatorRow, ...dataRows].join('\n');
                            }
                        } else if (sectionContent && typeof sectionContent === 'object') {
                            // If it's a structured object (like the one with .content), extract it
                            contentString = sectionContent.content || JSON.stringify(sectionContent);
                        }

                        const sectionPayload = {
                            "content": contentString,
                            "save_type": "SUBMIT"
                        };

                        const response = await updateSectionContent(rfpId, sectionId, sectionPayload);

                        if (response.status === 200) {
                            const data = response.data;

                            // Derive the saved-by name for peer notification toasts
                            const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
                            const savedByName =
                                savedUser.employee_name ||
                                savedUser.full_name ||
                                `${savedUser.first_name || ''} ${savedUser.last_name || ''}`.trim() ||
                                savedUser.email ||
                                'A collaborator';

                            // The save goes to the DRAFT (working copy). The main/approved content that
                            // read mode shows does not change until the Team Lead approves, so only record
                            // the new draft and put the approved text back into `editableData[section]`
                            // (while editing, that key carries the live draft).
                            setEditableData(prev => {
                                if (!prev) return prev;
                                let approvedText = prev[activeSection];
                                const updatedSections = (prev.sections || []).map(s => {
                                    const isMatch = (s.label && s.label === activeSection) ||
                                        (s.key && s.key === activeSection) ||
                                        (s.section_title && s.section_title === activeSection) ||
                                        (s.section_key && s.section_key === activeSection) ||
                                        (s.id && String(s.id) === String(sectionId)) ||
                                        (s.section_id && String(s.section_id) === String(sectionId));
                                    if (isMatch) {
                                        approvedText = s.content ?? s.text ?? approvedText;
                                        return { ...s, draft_content: contentString };
                                    }
                                    return s;
                                });

                                return {
                                    ...prev,
                                    [activeSection]: approvedText,
                                    sections: updatedSections,
                                };
                            });

                            setUpdateStatus({ type: 'success', message: data.message || 'Section content updated successfully!' });
                            setIsEditingContent(false);
                            setIsUpdating(false);

                            // Notify parent so it can broadcast the saved content to all peers
                            if (typeof onSaveSuccess === 'function') {
                                onSaveSuccess({
                                    sectionId,
                                    sectionKey: activeSection,
                                    content: contentString,
                                    savedByName,
                                });
                            }

                            // Refresh data from backend for all users (both leads and team members)
                            if (fetchRFPData) fetchRFPData(true);
                            if (fetchOverview) fetchOverview(setAssignments);
                            if (fetchVersions) fetchVersions();
                            return;
                        } else {
                            const data = response.data;
                            throw new Error(data.message || formatErrorDetail(data.detail) || 'Failed to update section content.');
                        }
                    }
                }

                // Fallback to legacy unified update for metadata/other fields
                const rawSourceDocs = editableData.source_documents || [];
                const sourceDocStrings = Array.isArray(rawSourceDocs)
                    ? rawSourceDocs.map(doc => typeof doc === 'string' ? doc : (doc?.name || doc?.filename || String(doc)))
                    : [];

                const payload = {
                    "PROPOSAL_TITLE": String(editableData.PROPOSAL_TITLE || editableData.proposal_title || ''),
                    "CLIENT_NAME": String(editableData.CLIENT_NAME || editableData.client_name || ''),
                    "CLIENT_LOCATION": String(editableData.CLIENT_LOCATION || editableData.client_place || ''),
                    "QUERY_DEADLINE": formatForDateInput(editableData.QUERY_DEADLINE || editableData.query_deadline) || '',
                    "PROPOSAL_DEADLINE": formatForDateInput(editableData.PROPOSAL_DEADLINE || editableData.proposal_deadline) || '',
                    "VALUATION": String(editableData.VALUATION || editableData.valuation || ''),
                    "CURRENCY": (() => {
                        const c = String(editableData.CURRENCY || editableData.currency || 'AED').toUpperCase();
                        return ['AED', 'USD', 'IND', 'EUR', 'SAR'].includes(c) ? c : 'AED';
                    })(),
                    "PROPOSAL_STAGE": String(editableData.PROPOSAL_STAGE || editableData.proposal_stage || 'Analyzed'),
                    "INDUSTRY": String(editableData.INDUSTRY || editableData.industry || 'Not Specified'),
                    "CONFIDENCE": parseFloat(editableData.CONFIDENCE || editableData.confidence) || 0.0,
                    "REASONING": String(editableData.REASONING || editableData.reasoning || ''),
                    "SLIDE_MAPPINGS": editableData.SLIDE_MAPPINGS || editableData.slide_mappings || {},
                    "rfp_id": rfpId,
                    "source_documents": sourceDocStrings
                };

                // Reverse map tabMapping to find keys for labels
                const reverseTabMapping = {};
                Object.entries(tabMapping).forEach(([key, label]) => {
                    reverseTabMapping[label] = key;
                });

                // Dynamically add all sections to the payload
                Object.keys(editableData).forEach(dataKey => {
                    if (!excludeKeys.includes(dataKey) && dataKey !== 'SECTION_STATUS' && dataKey !== 'section_status') {
                        const backendKey = reverseTabMapping[dataKey] || dataKey;
                        payload[backendKey] = String(editableData[dataKey] || '');
                    }
                });

                const response = await updateRFPAnalysis(rfpId, payload);

                if (response.status === 200) {
                    const data = response.data;
                    setUpdateStatus({ type: 'success', message: data.message || 'Analysis and Key Dates updated successfully!' });
                    setIsEditingContent(false);

                    // Forced Refresh for Leads
                    if (isManager) {
                        if (fetchRFPData) fetchRFPData(true);
                        if (fetchOverview) fetchOverview(setAssignments);
                        if (fetchVersions) fetchVersions();
                    }
                } else {
                    const data = response.data;
                    let errorMsg = formatErrorDetail(data.detail);
                    if (errorMsg.toLowerCase().includes('no changes were made')) {
                        setUpdateStatus({ type: 'success', message: data.message || 'Everything is already up to date!' });
                    } else {
                        setUpdateStatus({ type: 'error', message: errorMsg || 'Failed to update analysis.' });
                    }
                }
            } catch (err) {
                console.error('Update error:', err);
                setUpdateStatus({ type: 'error', message: err.message || 'Network error. Please try again.' });
            } finally {
                setIsUpdating(false);
                setTimeout(() => setUpdateStatus(null), 3000);
            }
        };

        const handleToggleSectionApproval = async (sectionKey) => {
            if (!rfpId || !sectionKey) return;

            const activeSectionObj = (editableData?.sections || []).find(
                s => s.label === sectionKey || s.key === sectionKey
            );
            const sectionId = activeSectionObj?.id || activeSectionObj?._id;

            if (!sectionId) {
                setUpdateStatus({ type: 'error', message: 'Could not identify section ID.' });
                return;
            }

            const currentStatus = editableData?.SECTION_STATUS || {};
            const isCurrentlyApproved = currentStatus[sectionKey] === 'Approved';
            const nextCode = isCurrentlyApproved ? 52 : 54;

            setIsApprovingSection(true);
            setUpdateStatus(null);

            try {
                const response = await updateSectionStatus(rfpId, sectionId, nextCode);

                if (response.status === 200 || response.status === 201) {
                    setEditableData(prev => {
                        const currentStatusDict = prev.SECTION_STATUS || {};
                        const currentStatusCodesDict = prev.SECTION_STATUS_CODE || {};
                        const updatedStatus = {
                            ...currentStatusDict,
                            [sectionKey]: isCurrentlyApproved ? 'Pending' : 'Approved'
                        };
                        const updatedStatusCodes = {
                            ...currentStatusCodesDict,
                            [sectionKey]: nextCode
                        };
                        const updatedSections = (prev.sections || []).map(sec => {
                            if (sec.label === sectionKey || sec.key === sectionKey) {
                                return {
                                    ...sec,
                                    status_code: nextCode,
                                    status: { code: nextCode, label: nextCode === 54 ? 'Section TL Approved' : 'Section In-Progress' },
                                    submitted_draft: nextCode === 54 ? null : sec.submitted_draft
                                };
                            }
                            return sec;
                        });
                        return {
                            ...prev,
                            sections: updatedSections,
                            SECTION_STATUS: updatedStatus,
                            section_status: updatedStatus,
                            SECTION_STATUS_CODE: updatedStatusCodes,
                            section_status_code: updatedStatusCodes
                        };
                    });

                    setUpdateStatus({ type: 'success', message: response.data?.message || 'Section approval status updated!' });

                    // Refresh data to broadcast state in background
                    if (fetchRFPData) await fetchRFPData(true);
                    if (fetchOverview) await fetchOverview(setAssignments);
                    if (fetchVersions) fetchVersions();
                }
            } catch (error) {
                console.error('Section approval error:', error);
                const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to update section approval.';
                setUpdateStatus({ type: 'error', message: formatErrorDetail(errorMsg) });
            } finally {
                setIsApprovingSection(false);
                setTimeout(() => setUpdateStatus(null), 3000);
            }
        };

        const handleSubmitToLead = async (activeSection, fetchAnalysis) => {
            if (!rfpId) return;
            const activeSectionObj = (editableData?.sections || []).find(s =>
                s.label === activeSection ||
                s.key === activeSection ||
                s.section_title === activeSection ||
                s.section_key === activeSection ||
                String(s.id) === String(activeSection) ||
                String(s.section_id) === String(activeSection)
            );
            const sectionId = activeSectionObj?.section_id || activeSectionObj?.id || activeSectionObj?._id;
            if (!sectionId) return;

            setIsSubmittingToLead(true);
            try {
                // Option A: Live editor state is saved by backend itself.
                // Do NOT call updateSectionContent/editor.getHTML() here to prevent modifying the main RFP document.
                const response = await submitSectionToLead(rfpId, sectionId);

                if (response.status === 200) {
                    setUpdateStatus({ type: 'success', message: 'Section submitted to Team Lead!' });

                    // Optimistically update local state so the UI changes immediately!
                    setEditableData(prev => {
                        if (!prev || !prev.sections) return prev;
                        const updatedSections = prev.sections.map(sec => {
                            if (sec.label === activeSection || sec.key === activeSection) {
                                return {
                                    ...sec,
                                    status_code: 53,
                                    status: { code: 53, label: 'Section Submitted for Review' }
                                };
                            }
                            return sec;
                        });
                        const currentStatusDict = prev.SECTION_STATUS || {};
                        const currentStatusCodesDict = prev.SECTION_STATUS_CODE || {};
                        const updatedStatus = {
                            ...currentStatusDict,
                            [activeSection]: 'Submitted'
                        };
                        const updatedStatusCodes = {
                            ...currentStatusCodesDict,
                            [activeSection]: 53
                        };
                        return {
                            ...prev,
                            sections: updatedSections,
                            SECTION_STATUS: updatedStatus,
                            section_status: updatedStatus,
                            SECTION_STATUS_CODE: updatedStatusCodes,
                            section_status_code: updatedStatusCodes
                        };
                    });

                    if (fetchAnalysis) fetchAnalysis(true);
                }
            } catch (error) {
                console.error('Submission error:', error);
                if (error.response?.status === 500) {
                    setUpdateStatus({ type: 'error', message: 'Could not save, please try again' });
                } else {
                    const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to submit section.';
                    setUpdateStatus({ type: 'error', message: formatErrorDetail(errorMsg) });
                }
            } finally {
                setIsSubmittingToLead(false);
                setTimeout(() => setUpdateStatus(null), 4000);
            }
        };

        const handleClarificationRequest = async (activeSection, notes) => {
            if (!rfpId || !activeSection) return;

            const activeSectionObj = (editableData?.sections || []).find(s => s.label === activeSection || s.key === activeSection);
            const sectionId = activeSectionObj?.id || activeSectionObj?._id;

            if (!sectionId) {
                setUpdateStatus({ type: 'error', message: 'Could not identify section ID.' });
                setTimeout(() => setUpdateStatus(null), 4000);
                return;
            }

            setIsStageUpdating(true);
            try {
                const response = await requestClarification(rfpId, sectionId, notes);

                if (response.status === 200) {
                    setAlertModal({
                        show: true,
                        message: response.data.message || 'Clarification request sent to team member!',
                        notes: notes
                    });

                    // Optimistically update local state so the UI changes immediately!
                    setEditableData(prev => {
                        if (!prev || !prev.sections) return prev;
                        const updatedSections = prev.sections.map(sec => {
                            if (sec.label === activeSection || sec.key === activeSection) {
                                return {
                                    ...sec,
                                    status_code: 55,
                                    status: { code: 55, label: 'Section Revision Needed' }
                                };
                            }
                            return sec;
                        });
                        const currentStatusDict = prev.SECTION_STATUS || {};
                        const currentStatusCodesDict = prev.SECTION_STATUS_CODE || {};
                        const updatedStatus = {
                            ...currentStatusDict,
                            [activeSection]: 'Revision'
                        };
                        const updatedStatusCodes = {
                            ...currentStatusCodesDict,
                            [activeSection]: 55
                        };
                        return {
                            ...prev,
                            sections: updatedSections,
                            SECTION_STATUS: updatedStatus,
                            section_status: updatedStatus,
                            SECTION_STATUS_CODE: updatedStatusCodes,
                            section_status_code: updatedStatusCodes
                        };
                    });

                    // Refresh data to show updated status in background
                    if (fetchRFPData) fetchRFPData(true);
                    if (fetchOverview) fetchOverview(setAssignments);
                    return true;
                }
            } catch (error) {
                console.error('Clarification request error:', error);
                const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to send clarification request.';
                setUpdateStatus({ type: 'error', message: formatErrorDetail(errorMsg) });
                setTimeout(() => setUpdateStatus(null), 4000);
                return false;
            } finally {
                setIsStageUpdating(false);
            }
        };

        const handleUpdateMetadata = async (metadataPayload) => {
            if (!rfpId) return;
            setIsUpdating(true);
            setUpdateStatus(null);

            try {
                const response = await updateRFPMetadata(rfpId, metadataPayload);

                if (response.status === 200) {
                    setUpdateStatus({ type: 'success', message: 'Key information updated successfully!' });

                    // Refresh data
                    if (fetchRFPData) await fetchRFPData(true);
                    if (fetchOverview) fetchOverview(setAssignments);

                    return true;
                } else {
                    setUpdateStatus({ type: 'error', message: 'Failed to update metadata.' });
                    return false;
                }
            } catch (err) {
                console.error('Metadata update error:', err);
                const errMsg = err.response?.data?.message || err.response?.data?.detail || 'Network error during metadata update.';
                setUpdateStatus({ type: 'error', message: formatErrorDetail(errMsg) });
                return false;
            } finally {
                setIsUpdating(false);
                setTimeout(() => setUpdateStatus(null), 3000);
            }
        };

        const handleRequestExtension = async (sectionId, requestedDate, reason) => {
            if (!rfpId || !sectionId || !requestedDate) return false;

            setIsUpdating(true);
            setUpdateStatus(null);

            try {
                const response = await requestExtension(rfpId, sectionId, requestedDate, reason);

                if (response.status === 200 || response.status === 201) {
                    setUpdateStatus({ type: 'success', message: response.data?.message || 'Deadline extension request submitted!' });

                    // Instantly update the local editableData state so the UI button updates immediately
                    setEditableData(prev => {
                        if (!prev || !prev.sections) return prev;
                        const updatedSections = prev.sections.map(sec => {
                            if (String(sec.id) === String(sectionId) || String(sec._id) === String(sectionId)) {
                                return {
                                    ...sec,
                                    extension_request: {
                                        employee_requested_deadline: requestedDate,
                                        employee_reason: reason,
                                        is_pending: true
                                    }
                                };
                            }
                            return sec;
                        });
                        return { ...prev, sections: updatedSections };
                    });

                    if (fetchRFPData) await fetchRFPData(true);
                    if (fetchOverview) fetchOverview(setAssignments);
                    return true;
                } else {
                    setUpdateStatus({ type: 'error', message: 'Failed to submit extension request.' });
                    return false;
                }
            } catch (error) {
                console.error('Extension request error:', error);
                const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to submit extension request.';
                setUpdateStatus({ type: 'error', message: formatErrorDetail(errorMsg) });
                return false;
            } finally {
                setIsUpdating(false);
                setTimeout(() => setUpdateStatus(null), 3000);
            }
        };

        const handleExtensionDecision = async (sectionId, action, newDeadline, notes) => {
            if (!rfpId || !sectionId) return false;

            setIsUpdating(true);
            setUpdateStatus(null);

            try {
                const payload = {
                    action: action, // 1 for Accept, 0 for Reject
                    notes: notes
                };

                // Only include new_deadline if accepting
                if (action === 1) {
                    payload.new_deadline = newDeadline;
                }

                const response = await handleSectionExtensionDecision(rfpId, sectionId, payload);


                if (response.status === 200) {
                    setUpdateStatus({ type: 'success', message: response.data?.message || `Extension ${action === 1 ? 'approved' : 'rejected'} successfully.` });
                    if (fetchRFPData) await fetchRFPData(true);
                    if (fetchOverview) fetchOverview(setAssignments);
                    return true;
                } else {
                    setUpdateStatus({ type: 'error', message: 'Failed to submit extension decision.' });
                    return false;
                }
            } catch (error) {
                console.error('Extension decision error:', error);
                const errorMsg = error.response?.data?.message || error.response?.data?.detail || 'Failed to submit decision.';
                setUpdateStatus({ type: 'error', message: formatErrorDetail(errorMsg) });
                return false;
            } finally {
                setIsUpdating(false);
                setTimeout(() => setUpdateStatus(null), 3000);
            }
        };

        return {
            isUpdating,
            isStageUpdating,
            updateStatus,
            setUpdateStatus,
            stageError,
            setStageError,
            handleStatusUpdate,
            executeStageTransition,
            handleUpdate,
            handleToggleSectionApproval,
            handleSubmitToLead,
            handleClarificationRequest,
            handleUpdateMetadata,
            handleRequestExtension,
            handleExtensionDecision,
            isSubmittingToLead,
            isApprovingSection
        };
    };
